import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Button,
  Grid,
  TextField,
  FormHelperText,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Stack,
  Box,
  Table,
  TableContainer,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { toast } from "react-toastify";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import FileDropzone from "components/Shared/FileDropzone";
import AppSwitch from "components/Shared/AppSwitch";
import SignaturePadComponent from "utils/SignaturePadComponent";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { getDateTimeAuto } from "utils/dateFormats";
import { useLazyGetDDLSendOutForDeliveryQuery } from "store/services/deliveryApi";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";
import {
  useGenerateOTPCodeMutation,
  useGenerateSecurityCodeMutation,
  useValidateCodeMutation,
} from "store/services/shipmentApi";

import { useAuth } from "hooks/useAuth";
import { useDOUploadFile } from "hooks";
import NCHLQR from "utils/generateNCHLQRCode";
import { getSocketInstance } from "socket/socket.io";

const timestamp = new Date()
  .toISOString()
  .replace(/[-:]/g, "")
  .replace("T", "_")
  .replace(/\..+/, "");

const INITIAL_STATE = {
  shipmentId: [],
  sendOutId: "",
  status: "Completed",
  attachments: [],
  documentUrl: "",
  signature: "",
  isActive: true,
  account: "",
  entity: "",
  receiveInBehalf: false,
  receiveBy: "",
  receiverPhone: "",
  deliveryLocation: { lat: null, lon: null },
  securityCode: "",
  securityAttachments: [],
};

const DeliveryForm = ({
  isBusy = false,
  deliveryDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin, loggedInUser } = useAuth();
  const isDriver = loggedInUser?.typeRefSource === "Driver";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { handleUploadFiles } = useDOUploadFile();
  const signaturePadRef = useRef(null);
  const nchlQRRef = useRef(null);
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const paymentSuccessHandled = useRef(false);
  const [signatureError, setSignatureError] = useState(false);
  const [isSecurityCodeDisabled, setIsSecurityCodeDisabled] = useState(false);
  const [isOTPCodeDisabled, setIsOTPCodeDisabled] = useState(false);
  const [securityCodeTimer, setSecurityCodeTimer] = useState(0);
  const [paymentResponseData, setPaymentResponseData] = useState(null);
  const [currentTraceId, setCurrentTraceId] = useState(null);
  // Tracks price IDs paid in the current form session. We can't rely
  // solely on the backend's `paymentStatus === "Paid"` flag because
  // the sendOuts re-fetch takes a few seconds (the websocket
  // messageHandler has to run first). Without this, the user would
  // see "Pay Via NCHL" still enabled for prices they've already paid.
  const [paidPriceIds, setPaidPriceIds] = useState(new Set());
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [otpCodeTimer, setOtpCodeTimer] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchSendOuts, { data: sentOuts, isLoading: loadingSentOuts }] =
    useLazyGetDDLSendOutForDeliveryQuery();
  const [generateOTPCode, { isLoading: loadingGenerateOTPCode }] =
    useGenerateOTPCodeMutation();
  const [generateSecurityCode, { isLoading: loadingGenerateSecurityCode }] =
    useGenerateSecurityCodeMutation();
  const [validateCode, { isLoading: loadingValidateCode }] =
    useValidateCodeMutation();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      actualDeliveryTime: getDateTimeAuto(),
      entity: viewEntity?.entity || "",
      account: viewEntity?.entity?.account || "",
      deliveryLocation: { lat: null, lon: null },
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchSendOutId = useWatch({ control, name: "sendOutId" });
  const _watchShipmentId = useWatch({ control, name: "shipmentId" });
  const _watchReceiveInBehalf = useWatch({ control, name: "receiveInBehalf" });
  const _watchReceiver = useWatch({ control, name: "receiver" });

  const filteredSendOuts =
    sentOuts?.filter((sendOut) =>
      sendOut.shipmentrequest?.some(
        (shipment) =>
          shipment.status === "SendOut" &&
          shipment.isConsolidated === false &&
          (shipment.isOnHold ?? false) !== true
      )
    ) || [];

  const selectedSendOut = filteredSendOuts?.find(
    (so) => so?._id === _watchSendOutId?._id
  );

  const unpaidPriceIds = useMemo(() => {
    if (!_watchShipmentId?.length) return [];
    return _watchShipmentId
      .flatMap((s) => {
        const shipment =
          selectedSendOut?.shipmentrequest?.find(
            (ws) => ws._id === (s._id || s)
          ) || s;
        return (shipment?.priceItems || [])
          .filter((pi) => pi.paymentStatus !== "Paid")
          .map((pi) => pi._id);
      })
      .filter(Boolean);
  }, [_watchShipmentId, selectedSendOut]);

  const selectedShipment =
    _watchShipmentId?.length > 0 ? _watchShipmentId[0] : null;
  const selectedShipmentDetails =
    selectedSendOut?.shipmentrequest?.find(
      (s) => s._id === selectedShipment?._id
    ) || selectedShipment;

  const deliverySecurity =
    selectedShipmentDetails?.deliverySecurity || "Normal";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shipmentIds = deliveryDetail?.shipmentId
    ? Array.isArray(deliveryDetail.shipmentId)
      ? deliveryDetail.shipmentId
      : [deliveryDetail.shipmentId]
    : [];

  const sendOutShipments = deliveryDetail
    ? [
      ...(selectedSendOut?.shipmentrequest || []),
      ...(shipmentIds
        .filter(
          (id) =>
            !selectedSendOut?.shipmentrequest?.some(
              (s) => s._id === (id._id || id)
            )
        )
        .map((id) => ({
          _id: id._id || id,
          shipmentId: id.shipmentId || `Unknown (${id._id || id})`,
          IsReturning: id.IsReturning || false,
          status: id.status || "Unknown",
          receiver: id.receiver || null,
          deliverySecurity: id.deliverySecurity || "Normal",
        })) || []),
    ].filter(
      (shipment) =>
        !_watchReceiver || shipment.receiver === _watchReceiver?._id
    )
    : (selectedSendOut?.shipmentrequest || []).filter(
      (shipment) =>
        shipment.status === "SendOut" &&
        (shipment.isOnHold ?? false) !== true &&
        (!_watchReceiver || shipment.receiver === _watchReceiver?._id)
    );

  const getTotalGrand = () => {
    if (!_watchShipmentId?.length) return 0;

    return _watchShipmentId.reduce((total, shipment) => {
      const selectedShipment =
        selectedSendOut?.shipmentrequest?.find(
          (s) => s._id === (shipment._id || shipment)
        ) || shipment;
      const priceItemsTotal =
        selectedShipment?.priceItems?.reduce(
          (sum, item) => {
            // Skip items already paid (either backend-confirmed or
            // locally tracked via paidPriceIds).
            if (
              item.paymentStatus === "Paid" ||
              paidPriceIds.has(String(item._id))
            ) {
              return sum;
            }
            return sum + (item.charge || 0);
          },
          0
        ) || 0;
      return total + priceItemsTotal;
    }, 0);
  };

  // Sum of charges for prices already paid in the current session.
  // Shown in the "Payment successful" indicator next to the NCHL button.
  const getTotalPaid = () => {
    if (!_watchShipmentId?.length) return 0;
    return _watchShipmentId.reduce((total, shipment) => {
      const selectedShipment =
        selectedSendOut?.shipmentrequest?.find(
          (s) => s._id === (shipment._id || shipment)
        ) || shipment;
      return (
        total +
        (selectedShipment?.priceItems || [])
          .filter((item) => paidPriceIds.has(String(item._id)))
          .reduce((sum, item) => sum + (item.charge || 0), 0)
      );
    }, 0);
  };

  const handleNCHLPaymentSuccess = useCallback((response) => {
    if (paymentSuccessHandled.current) return;
    paymentSuccessHandled.current = true;
    let paymentData = null;
    if (response?.paymentData) {
      paymentData = response.paymentData;
    } else if (response?.sessionSrlNo || response?.debitStatus) {
      // Raw NCHL status only — the debit is confirmed but the backend
      // hasn't finished creating the collection record yet (that only
      // happens via the websocket messageHandler). Don't treat this as
      // terminal: there's no redirect data yet, and locking the guard
      // here would silently swallow the real socket event when it
      // arrives. Reset the guard and bail so the follow-up call from
      // the socket can still do the work.
      paymentSuccessHandled.current = false;
      toast.info("Payment confirmed, finalizing collection...");
      return;
    }

    if (paymentData) {
      setPaymentResponseData(paymentData);
      toast.success("NCHL Payment successful!");
      nchlQRRef.current?.handleCloseQR();
      setIsPaymentDone(true);

      // Track paid price IDs locally so the UI updates immediately —
      // the backend's sendOuts re-fetch won't reflect "Paid" status
      // for a few seconds (the websocket messageHandler has to run
      // first to mark ShipmentPrice as Paid in the DB). Without this,
      // the "Pay Via NCHL" button would stay enabled and the user
      // could pay for the same prices again.
      //
      // The COC socket payload includes `paymentData.priceIds` directly.
      // The DELIVERY socket payload does NOT (see backend
      // nchl.websocket.js socketResponseData — only COC adds priceIds).
      // Fall back to marking the currently-unpaid prices of the selected
      // shipments as paid, since those are exactly the prices the user
      // just paid for (the NCHL dialog was passed unpaidPriceIds).
      const paidIdsFromPayload = Array.isArray(paymentData.priceIds)
        ? paymentData.priceIds.map(String)
        : [];
      const idsToMark =
        paidIdsFromPayload.length > 0
          ? paidIdsFromPayload
          : unpaidPriceIds.map(String);
      if (idsToMark.length > 0) {
        setPaidPriceIds((prev) => {
          const next = new Set(prev);
          idsToMark.forEach((id) => next.add(id));
          return next;
        });
      }

      if (_watchAccount?._id && _watchEntity?._id) {
        // Extended delay (was 800ms) to give the backend websocket
        // messageHandler time to write the Paid status to the DB before
        // we re-fetch sendOuts — otherwise the refresh races the DB
        // write and shows stale "Not Paid" status.
        const timer = setTimeout(() => {
          fetchSendOuts({
            account: _watchAccount._id,
            entity: _watchEntity._id,
            timestamp: Date.now(),
          });
          paymentSuccessHandled.current = false;
        }, 2500);
        return () => clearTimeout(timer);
      } else {
        paymentSuccessHandled.current = false;
      }
    } else {
      paymentSuccessHandled.current = false;
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchSendOuts, unpaidPriceIds]);

  useEffect(() => {
    setIsPaymentDone(false);
    setPaymentResponseData(null);
    // Clear locally-tracked paid IDs so a new shipment starts fresh.
    setPaidPriceIds(new Set());
    // Allow a fresh NCHL payment attempt to re-fire onSuccess now that
    // the selected shipment has changed.
    paymentSuccessHandled.current = false;
  }, [_watchShipmentId]);


  useEffect(() => {
    // Trace-scoped socket listener. The previous implementation listened
    // on the global "payment-success/delivery" event, but the backend
    // routes that event to the OPEN socket.io instance (`/open-socket`
    // path) — which the frontend's socket.io.js does NOT connect to.
    // The frontend connects to the regular instance, which receives
    // "payment-success/delivery-collection/<traceId>" (and the COC
    // equivalent). Without this, no delivery-payment socket event ever
    // reaches the form, prices stay "Not Paid", and "Pay Via NCHL"
    // remains clickable for the same shipment.
    if (!currentTraceId) return;
    const socket = getSocketInstance();
    if (!socket) return;

    const uniqueEvent = `payment-success/delivery-collection/${currentTraceId}`;
    const handleSuccess = (response) => {
      if (response.validationTraceId !== currentTraceId) return;
      handleNCHLPaymentSuccess(response);
    };

    socket.on(uniqueEvent, handleSuccess);

    return () => {
      // ONLY remove the listener here. Do NOT reset currentTraceId or
      // paymentSuccessHandled in cleanup — React.StrictMode mounts/
      // unmounts effects twice in dev, and resetting state here would
      // break the listener on the second mount. The _watchShipmentId
      // effect above is the proper reset point.
      socket.off(uniqueEvent, handleSuccess);
    };
  }, [currentTraceId, handleNCHLPaymentSuccess]);

  const receivers = Array.from(
    new Map(
      sendOutShipments
        .filter((shipment) => shipment.receiver && shipment.receiverDetails)
        .map((shipment) => [
          shipment.receiver,
          {
            _id: shipment.receiver,
            name: shipment.receiverDetails?.name || "Unknown",
          },
        ])
    ).values()
  );

  useEffect(() => {
    let timer;
    if (isSecurityCodeDisabled && securityCodeTimer > 0) {
      timer = setInterval(() => {
        setSecurityCodeTimer((prev) => {
          if (prev <= 1) {
            setIsSecurityCodeDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isSecurityCodeDisabled, securityCodeTimer]);

  useEffect(() => {
    let timer;
    if (isOTPCodeDisabled && otpCodeTimer > 0) {
      timer = setInterval(() => {
        setOtpCodeTimer((prev) => {
          if (prev <= 1) {
            setIsOTPCodeDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isOTPCodeDisabled, otpCodeTimer]);

  const handleGenerateOTPCode = async () => {
    if (!selectedShipment || isOTPCodeDisabled) return;
    try {
      setIsOTPCodeDisabled(true);
      setOtpCodeTimer(30);
      await generateOTPCode(selectedShipment._id).unwrap();
      toast.success("OTP Code Sent to Mail!");
    } catch (error) {
      setIsOTPCodeDisabled(false);
      setOtpCodeTimer(0);
      toast.error(
        error?.data?.message ?? error?.message ?? "Couldn't generate OTP Code!"
      );
    }
  };

  const handleGenerateSecurityCode = async () => {
    if (!selectedShipment || isSecurityCodeDisabled) return;
    try {
      setIsSecurityCodeDisabled(true);
      setSecurityCodeTimer(30);
      await generateSecurityCode(selectedShipment._id).unwrap();
      toast.success("Security Code Sent to Mail!");
    } catch (error) {
      setIsSecurityCodeDisabled(false);
      setSecurityCodeTimer(0);
      toast.error(
        error?.data?.message ??
        error?.message ??
        "Couldn't generate Security Code!"
      );
    }
  };

  useEffect(() => {
    if (isDriver && !deliveryDetail) {
      const getCurrentLocation = () => {
        if (!navigator.geolocation) {
          setValue("deliveryLocation.lat", null);
          setValue("deliveryLocation.lon", null);
          toast.error("Geolocation is not supported by this browser.");
          return;
        }

        navigator.permissions
          .query({ name: "geolocation" })
          .then((permissionStatus) => {
            if (permissionStatus.state === "denied") {
              setValue("deliveryLocation.lat", null);
              setValue("deliveryLocation.lon", null);
              toast.error(
                "Location access denied. Please enable location in your browser settings."
              );
              return;
            }

            navigator.geolocation.getCurrentPosition(
              (position) => {
                const currentLat = position.coords.latitude;
                const currentLon = position.coords.longitude;
                setValue("deliveryLocation.lat", currentLat);
                setValue("deliveryLocation.lon", currentLon);
              },
              (error) => {
                console.error("Failed to fetch location:", error.message);
                setValue("deliveryLocation.lat", null);
                setValue("deliveryLocation.lon", null);
                toast.error(`Failed to fetch location: ${error.message}`);
              },
              { timeout: 10000, maximumAge: 60000 }
            );
          })
          .catch((error) => {
            console.error("Error checking geolocation permission:", error);
            setValue("deliveryLocation.lat", null);
            setValue("deliveryLocation.lon", null);
            toast.error("Error accessing geolocation permissions.");
          });
      };

      getCurrentLocation();
    }
  }, [isDriver, deliveryDetail, setValue]);

  useEffect(() => {
    if (deliveryDetail && sentOuts) {
      const sendOutIdToMatch =
        deliveryDetail?.sendOutId?._id || deliveryDetail?.sendOutId || "";
      if (!sendOutIdToMatch) {
        setValue("sendOutId", "");
        return;
      }

      const matchingSendOut = sentOuts.find(
        (so) => so._id === sendOutIdToMatch
      );
      setValue(
        "sendOutId",
        matchingSendOut || {
          _id: sendOutIdToMatch,
          sendOutId:
            deliveryDetail?.sendOutId?.sendOutId ||
            `Unknown (${sendOutIdToMatch})`,
        }
      );
    }
  }, [deliveryDetail, sentOuts, setValue]);

  useEffect(() => {
    if (deliveryDetail && sentOuts) {
      const shipments = shipmentIds.map((shipmentId) => {
        const shipment = selectedSendOut?.shipmentrequest?.find(
          (s) => s._id === (shipmentId._id || shipmentId)
        );
        return (
          shipment || {
            _id: shipmentId._id || shipmentId,
            shipmentId:
              shipmentId.shipmentId ||
              `Unknown (${shipmentId._id || shipmentId})`,
            IsReturning: shipmentId.IsReturning || false,
            status: shipmentId.status || "Unknown",
            receiver: shipmentId.receiver || null,
            deliverySecurity: shipmentId.deliverySecurity || "Normal",
            uploadedId: shipmentId.uploadedId || [],
          }
        );
      });
      setValue("shipmentId", shipments);

      if (deliveryDetail?.securityDetails) {
        setValue("securityCode", deliveryDetail.securityDetails.value || "");
      }

      const firstShipment = shipments[0];
      if (firstShipment?.uploadedId?.length > 0) {
        setValue("securityAttachments", firstShipment.uploadedId);
      }
    }
  }, [deliveryDetail, sentOuts, selectedSendOut, shipmentIds, setValue]);

  const convertDataUrlToFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const handleOnSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const signatureDataUrl = signaturePadRef?.current?.getSignature();
      if (!signatureDataUrl) {
        setSignatureError(true);
        return;
      }
      setSignatureError(false);

      const signatureFile = convertDataUrlToFile(
        signatureDataUrl,
        `delivery_signature_${timestamp}.png`
      );

      let securityDetails = { type: deliverySecurity };
      if (["Security Code", "OTP"].includes(deliverySecurity)) {
        if (!data.securityCode) {
          toast.error(`${deliverySecurity} is required.`);
          return;
        }
        try {
          await validateCode({
            shipmentId: selectedShipment._id,
            code: data.securityCode,
            codeType: deliverySecurity,
          }).unwrap();
        } catch (error) {
          toast.error(error?.data?.error || `Invalid ${deliverySecurity}!`);
          return;
        }
        securityDetails.value = data.securityCode;
      } else if (deliverySecurity === "ID") {
        if (!data.securityAttachments?.length) {
          toast.error("ID verification document is required.");
          return;
        }
      }

      const deliveryData = {
        ...data,
        account: data?.account?._id,
        entity: data?.entity?._id,
        sendOutId: data?.sendOutId?._id,
        shipmentId: data?.shipmentId?.map((s) => s._id),
        deliveryLocation: data?.deliveryLocation,
        securityDetails,
        fulfillmentCompletionId:
          data.fulfillmentCompletionId || `FC_${timestamp}`,
      };

      const [existingAttachments, newAttachments] = data?.attachments?.reduce(
        ([pass, fail], el) =>
          el?.publicId || el?.url
            ? [[...pass, el], fail]
            : [pass, [...fail, el]],
        [[], []]
      );

      const [existingSecurityAttachments, newSecurityAttachments] =
        data?.securityAttachments?.reduce(
          ([pass, fail], el) =>
            el?.publicId || el?.url || el?.secureUrl
              ? [[...pass, el], fail]
              : [pass, [...fail, el]],
          [[], []]
        );

      const uploadPromises = [];

      uploadPromises.push(
        handleUploadFiles([signatureFile], {
          folderName: "DO_FOLDER_NAME.DELIVERY_SIGNATURE",
          entity: viewEntity?.entity?._id,
          account: viewEntity?.entity?.account?._id,
        })
      );

      if (newAttachments?.length) {
        uploadPromises.push(
          handleUploadFiles(newAttachments, {
            folderName: "SHIPMENT_COMPLETION",
            entity: viewEntity?.entity?._id,
            account: viewEntity?.entity?.account?._id,
          })
        );
      }

      if (newSecurityAttachments?.length) {
        uploadPromises.push(
          handleUploadFiles(newSecurityAttachments, {
            folderName: "DELIVERY_SECURITY_ID",
            entity: viewEntity?.entity?._id,
            account: viewEntity?.entity?.account?._id,
          })
        );
      }

      const uploadResults = await Promise.all(uploadPromises);

      const signatureUpload = uploadResults[0];
      const attachmentsUpload = newAttachments?.length
        ? uploadResults[1]
        : null;
      const securityAttachmentsUpload = newSecurityAttachments?.length
        ? uploadResults[newAttachments?.length ? 2 : 1]
        : null;

      deliveryData.signature = signatureUpload.uploadedFiles[0]?.url || "";
      deliveryData.attachments = [
        ...existingAttachments,
        ...(attachmentsUpload?.uploadedFiles || []),
      ].map((attachment) => ({
        name: attachment.name || "",
        type: attachment.type || "",
        size: attachment.size || 0,
        publicId: attachment.publicId || "",
        url: attachment.secureUrl || attachment.url || "",
      }));

      deliveryData.securityAttachments = [
        ...existingSecurityAttachments,
        ...(securityAttachmentsUpload?.uploadedFiles || []),
      ].map((attachment) => ({
        name: attachment.name || "",
        type: attachment.type || "",
        size: attachment.size || 0,
        publicId: attachment.publicId || "",
        url: attachment.secureUrl || attachment.url || "",
      }));

      let shipmentUpdateData = null;
      if (deliverySecurity === "ID") {
        shipmentUpdateData = {
          shipmentId: selectedShipment._id,
          uploadedId: [
            ...existingSecurityAttachments,
            ...(securityAttachmentsUpload?.uploadedFiles || []),
          ].map((attachment) => ({
            name: attachment.name || "",
            type: attachment.type || "",
            size: attachment.size || 0,
            publicId: attachment.publicId || "",
            url: attachment.secureUrl || attachment.url || "",
          })),
        };
      }
      await onSubmit({
        deliveryData,
        shipmentUpdateData,
        paymentData: paymentResponseData
      });
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.message || "Failed to submit form");
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!deliveryDetail) return;

    const sendOutIdValue =
      deliveryDetail?.sendOutId?._id || deliveryDetail?.sendOutId || "";
    const shipmentIdValue = shipmentIds;

    reset({
      ...INITIAL_STATE,
      ...deliveryDetail,
      actualDeliveryTime: deliveryDetail?.actualDeliveryTime
        ? new Date(
          new Date(deliveryDetail.actualDeliveryTime).getTime() +
          (5 * 60 + 45) * 60 * 1000
        )
          .toISOString()
          .slice(0, 16)
        : getDateTimeAuto(),
      sendOutId: sendOutIdValue
        ? {
          _id: sendOutIdValue,
          sendOutId: deliveryDetail?.sendOutId?.sendOutId || "Unknown",
        }
        : "",
      shipmentId: shipmentIdValue.map((shipmentId) => ({
        _id: shipmentId._id || shipmentId,
        shipmentId: shipmentId.shipmentId || "Unknown",
        IsReturning: shipmentId.IsReturning || false,
        status: shipmentId.status || "Unknown",
        receiver: shipmentId.receiver || null,
        deliverySecurity: shipmentId.deliverySecurity || "Normal",
        uploadedId: shipmentId.uploadedId || [],
      })),
      account: deliveryDetail?.account || "",
      entity: deliveryDetail?.entity || "",
      deliveryLocation: deliveryDetail?.deliveryLocation || {
        lat: null,
        lon: null,
      },
      securityCode: deliveryDetail?.securityDetails?.value || "",
      securityAttachments: shipmentIdValue[0]?.uploadedId || [],
      receiver:
        shipmentIdValue[0]?.receiver && shipmentIdValue[0]?.receiverDetails
          ? {
            _id: shipmentIdValue[0].receiver,
            name: shipmentIdValue[0].receiverDetails?.name || "Unknown",
          }
          : "",
    });

    const accountId =
      deliveryDetail?.account?._id || deliveryDetail?.account || "";
    if (accountId && (isAccountAdmin || isSystemAdmin)) {
      fetchEntities({ account: accountId });
    }

    const entityId =
      deliveryDetail?.entity?._id || deliveryDetail?.entity || "";
    if (entityId) {
      fetchSendOuts({
        account: accountId,
        entity: entityId,
        timestamp: new Date().getTime(),
      });
    }
  }, [
    deliveryDetail,
    reset,
    fetchEntities,
    fetchSendOuts,
    isAccountAdmin,
    isSystemAdmin,
    shipmentIds,
  ]);

  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  useEffect(() => {
    if (!_watchEntity?._id || deliveryDetail) return;
    fetchSendOuts({
      account: _watchAccount?._id,
      entity: _watchEntity._id,
      timestamp: new Date().getTime(),
    });
  }, [_watchAccount?._id, _watchEntity?._id, fetchSendOuts, deliveryDetail]);

  return (
    <AppForm loading={isBusy || loadingValidateCode}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {/* Visible "Payment successful" indicator with the amount paid
              for the selected shipment. Placed at the top of the form so
              it's the first thing the user sees after a successful NCHL
              payment — shows immediately when the socket event fires,
              doesn't wait for the sendOuts re-fetch to confirm Paid
              status in the DB. */}
          {isPaymentDone && paidPriceIds.size > 0 && (
            <Grid item xs={12}>
              <div
                className="inline-flex items-center gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800"
                data-testid="nchl-payment-success-indicator"
              >
                <span className="text-base font-semibold leading-none">✓</span>
                <span>
                  Payment successful — Rs{" "}
                  <span className="font-semibold">
                    {getTotalPaid().toFixed(2)}
                  </span>{" "}
                  paid for shipment{" "}
                  <span className="font-semibold">
                    {selectedShipmentDetails?.shipmentId}
                  </span>
                  . Pending amount: Rs{" "}
                  <span className="font-semibold">
                    {getTotalGrand().toFixed(2)}
                  </span>
                  .
                </span>
              </div>
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                      setValue("sendOutId", "");
                      setValue("shipmentId", []);
                      setValue("receiver", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("sendOutId", "");
                      setValue("shipmentId", []);
                      setValue("receiver", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    disabled
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Controller
              name="sendOutId"
              control={control}
              rules={{ required: "Please select a Send Out." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Send Out"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("shipmentId", []);
                    setValue("receiver", "");
                  }}
                  getOptionLabel={(option) => option?.sendOutId || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === (value?._id || value)
                  }
                  options={filteredSendOuts}
                  loading={loadingSentOuts}
                  loadingText="Loading send out ..."
                  helperText={
                    errors?.sendOutId?.message ||
                    (!loadingSentOuts &&
                      (!filteredSendOuts || filteredSendOuts.length === 0)
                      ? "No send-out available"
                      : "")
                  }
                  error={!!errors?.sendOutId}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="receiver"
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Receiver"
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("shipmentId", []);
                  }}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={receivers}
                  loading={loadingSentOuts}
                  loadingText="Loading receivers..."
                  helperText={
                    errors?.receiver?.message ||
                    (!loadingSentOuts && (!receivers || receivers.length === 0)
                      ? "No receivers available"
                      : "")
                  }
                  error={!!errors?.receiver}
                  disabled={!!deliveryDetail || !sendOutShipments?.length}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select at least one Shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  multiple
                  fullWidth
                  label="Shipments"
                  required
                  value={value ?? []}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option?.shipmentId
                      ? option.IsReturning
                        ? `${option.shipmentId} (R)`
                        : option.shipmentId
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === (value?._id || value)
                  }
                  options={sendOutShipments}
                  loading={loadingSentOuts}
                  loadingText="Loading shipment IDs..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                  disabled={!_watchSendOutId || !!deliveryDetail}
                />
              )}
            />
          </Grid>
          {selectedShipment && (
            <Grid
              item
              xs={12}
              sm={2}
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {deliverySecurity === "Security Code" && (
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    loadingGenerateSecurityCode ||
                    !!deliveryDetail ||
                    isSecurityCodeDisabled
                  }
                  onClick={() => {
                    if (
                      !loadingGenerateSecurityCode &&
                      !isSecurityCodeDisabled
                    ) {
                      handleGenerateSecurityCode();
                    }
                  }}
                >
                  {loadingGenerateSecurityCode
                    ? "Generating..."
                    : isSecurityCodeDisabled
                      ? `Wait ${securityCodeTimer}s`
                      : "Generate Security Code"}
                </Button>
              )}
              {deliverySecurity === "OTP" && (
                <Button
                  variant="contained"
                  disabled={
                    loadingGenerateOTPCode ||
                    !!deliveryDetail ||
                    isOTPCodeDisabled
                  }
                  onClick={() => {
                    if (!loadingGenerateOTPCode && !isOTPCodeDisabled) {
                      handleGenerateOTPCode();
                    }
                  }}
                >
                  {loadingGenerateOTPCode
                    ? "Generating..."
                    : isOTPCodeDisabled
                      ? `Wait ${otpCodeTimer}s`
                      : "Generate OTP Code"}
                </Button>
              )}
            </Grid>
          )}
          {selectedShipment &&
            ["Security Code", "OTP"].includes(deliverySecurity) && (
              <Grid item xs={12} sm={4}>
                <Controller
                  name="securityCode"
                  control={control}
                  rules={{
                    required: `${deliverySecurity} is required`,
                  }}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      required
                      label={deliverySecurity}
                      {...field}
                      error={!!errors.securityCode}
                      helperText={errors.securityCode?.message}
                      disabled={!!deliveryDetail}
                    />
                  )}
                />
              </Grid>
            )}
          {selectedShipment && deliverySecurity === "ID" && (
            <Grid item xs={12}>
              <FormHelperText>Security Documents</FormHelperText>
              <Controller
                name="securityAttachments"
                control={control}
                rules={{
                  validate: (value) =>
                    value?.length > 0 || "ID document is required.",
                }}
                render={({ field }) => (
                  <FileDropzone
                    multiple
                    maxFiles={2}
                    files={field.value || []}
                    onFileChange={(files) => {
                      setValue("securityAttachments", files);
                      trigger("securityAttachments");
                    }}
                    error={!!errors.securityAttachments}
                    helperText={errors.securityAttachments?.message || ""}
                    showError={isSubmitted}
                    acceptedFileTypes={{
                      "image/*": [".jpeg", ".jpg", ".png"],
                      "application/pdf": [".pdf"],
                    }}
                    disabled={!!deliveryDetail}
                  />
                )}
              />
            </Grid>
          )}
          {_watchShipmentId?.length > 0 && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ boxShadow: 2, borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Shipment Details
                  </Typography>

                  <div className="mb-4 rounded border border-gray-200 bg-gray-100 p-4 dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-base font-medium">
                      <strong>Total Payment Amount:</strong> Rs.{" "}
                      {getTotalGrand().toFixed(2)}
                    </p>
                  </div>

                  {_watchShipmentId.map((shipment, index) => {
                    const selectedShipment =
                      selectedSendOut?.shipmentrequest?.find(
                        (s) => s._id === (shipment._id || shipment)
                      ) || shipment;

                    const senderDetails =
                      selectedShipment?.customerDetails &&
                        Object.keys(selectedShipment.customerDetails).length > 0
                        ? selectedShipment.customerDetails
                        : selectedShipment?.vendorDetails;

                    const receiverDetails = selectedShipment?.receiverDetails;

                    const validPriceItems =
                      selectedShipment?.priceItems?.filter(
                        (item) => Object.keys(item).length > 0
                      ) || [];

                    return (
                      <Box
                        key={selectedShipment?._id || index}
                        sx={{ mb: 2, pb: 2, borderBottom: "1px solid #eee" }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>
                                Shipment:{" "}
                                {selectedShipment?.shipmentId || "Unknown"}
                              </strong>
                            </Typography>

                            <Typography
                              variant="body2"
                              fontWeight={600}
                              gutterBottom
                            >
                              Sender
                            </Typography>
                            {senderDetails ? (
                              <Stack spacing={0.5}>
                                <Typography variant="body2">
                                  <strong>Name:</strong>{" "}
                                  {senderDetails.name || "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Phone:</strong>{" "}
                                  {senderDetails.phone || "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Address:</strong>{" "}
                                  {senderDetails.address || "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Payment Amount:</strong> Rs.{" "}
                                  {(
                                    selectedShipment?.priceItems?.reduce(
                                      (sum, item) => sum + (item.charge || 0),
                                      0
                                    ) || 0
                                  ).toFixed(2)}
                                </Typography>
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No sender details available
                              </Typography>
                            )}
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              gutterBottom
                            >
                              Receiver
                            </Typography>
                            <Stack spacing={0.5}>
                              <Typography variant="body2">
                                <strong>Name:</strong>{" "}
                                {receiverDetails?.customerUser?.name ||
                                  receiverDetails?.name ||
                                  "N/A"}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Phone:</strong>{" "}
                                {receiverDetails?.phone || "N/A"}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Address:</strong>{" "}
                                {receiverDetails?.address || "N/A"}
                              </Typography>
                              <Typography variant="body2">
                                <strong>E-mail:</strong>{" "}
                                {receiverDetails?.email || "N/A"}
                              </Typography>
                            </Stack>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              gutterBottom
                              sx={{ mt: 2 }}
                            >
                              Price Items
                            </Typography>
                            {validPriceItems.length > 0 ? (
                              <TableContainer component={Paper} sx={{ mt: 1 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Fee Item</TableCell>
                                      <TableCell>Charge Type</TableCell>
                                      <TableCell>Charge</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {validPriceItems.map((priceItem) => (
                                      <TableRow key={priceItem._id}>
                                        <TableCell>
                                          {priceItem.feeItem || "-"}
                                        </TableCell>
                                        <TableCell>
                                          {priceItem.chargeType || "-"}
                                        </TableCell>
                                        <TableCell>
                                          Rs.{" "}
                                          {priceItem.charge?.toFixed(2) || "-"}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No unpaid price items available
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Controller
                  name="actualDeliveryTime"
                  control={control}
                  rules={{ required: "Actual Delivery Time is required." }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      type="datetime-local"
                      label="Actual Delivery Time"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.actualDeliveryTime}
                      helperText={errors.actualDeliveryTime?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Controller
                  name="receiveInBehalf"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            if (!e.target.checked) {
                              setValue("receiveBy", "");
                              setValue("receiverPhone", "");
                            }
                          }}
                        />
                      }
                      label="Receive In Behalf ?"
                    />
                  )}
                />
              </Grid>
              {_watchReceiveInBehalf && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="receiveBy"
                      control={control}
                      rules={{ required: "Please provide receiver Name." }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          fullWidth
                          size="small"
                          label="Receiver Name"
                          placeholder="Enter Receiver Name"
                          error={!!errors?.receiveBy}
                          helperText={errors?.receiveBy?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="receiverPhone"
                      control={control}
                      rules={{
                        required: "Receiver Phone is required",
                        validate: (value) => {
                          const digits = value.replace(/\D/g, "");
                          if (digits.length < 7 || digits.length > 17) {
                            return "Phone number must be between 9 and 16 digits";
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <CustomCountryPhoneInput
                          placeholder="Receiver Phone Number"
                          label="Receiver Phone"
                          {...field}
                          lockedCountry={{ idd: "+977", cca2: "NP" }}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div
              style={{ display: "flex", alignItems: "stretch", height: "100%" }}
            >
              <SignaturePadComponent
                label="Signature"
                ref={signaturePadRef}
                InputLabelProps={{ shrink: true }}
                width={640}
                height={isMobile ? 390 : 260}
              />
            </div>
            {signatureError && (
              <FormHelperText error>
                Please sign before submitting.
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormHelperText>Delivery Attachments</FormHelperText>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value || []}
                  onFileChange={(files) => setValue("attachments", files)}
                  disabled={!!deliveryDetail}
                />
              )}
            />

            <div className="mt-2 flex items-center gap-2">
              <Button
                variant="outlined"
                color="success"
                onClick={async () => {
                  const traceId = await nchlQRRef?.current?.handleGenerateQR();
                  if (traceId) setCurrentTraceId(traceId);
                }}
                disabled={
                  isSubmitting ||
                  !selectedShipment ||
                  getTotalGrand() <= 0 ||
                  unpaidPriceIds.length === 0 ||
                  !!deliveryDetail ||
                  isPaymentDone
                }
              >
                {getTotalGrand() <= 0 || isPaymentDone
                  ? "Payment"
                  : "Pay Via NCHL"}
              </Button>

              <NCHLQR
                ref={nchlQRRef}
                amount={getTotalGrand()}
                priceIds={unpaidPriceIds}
                shipmentId={selectedShipmentDetails?.shipmentId || ""}
                onSuccess={handleNCHLPaymentSuccess}
              />
            </div>
          </Grid>
          {deliveryDetail && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      {openConfirmation && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Card sx={{ p: 3, minWidth: { xs: 280, sm: 400 }, maxWidth: "92vw" }}>
            <Typography variant="h6" gutterBottom>
              Confirm Payment
            </Typography>
            <Typography>
              You are going to pay Rs {getTotalGrand().toFixed(2)}. Proceed with
              payment?
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setOpenConfirmation(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setOpenConfirmation(false);
                  handleSubmit(handleOnSubmit)();
                }}
              >
                Confirm
              </Button>
            </Box>
          </Card>
        </Box>
      )}

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            const amount = getTotalGrand();
            if (amount > 0) {
              setOpenConfirmation(true);
            } else {
              handleSubmit(handleOnSubmit)();
            }
          }}
          disabled={isBusy || loadingValidateCode || isSubmitting}
        >
          {deliveryDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default DeliveryForm;